import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";
import {
  Control,
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { match } from "ts-pattern";
import { HRQuestion, HRQuestionGroup } from "../../messages/HR";
import { getQuestionText, isAnswerableQuestion } from "../Onboarding/utils/onboardingUtils";
import OnboardingQuestion from "./OnboardingQuestion";
import Question from "./Question";
import { HRQuestionTemplateId } from "../../messages";

interface Props {
  questionGroup: HRQuestionGroup;
  title: string;
  language: string;
  control: Control;
  isReadOnly: boolean;
  isSignaureDisabled: boolean;
  emailVerificationTemplateId: HRQuestionTemplateId | null;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  onError: (params: { title: string; message: string }) => void;
}

function OnboardingQuestionGroup(props: Props) {
  const [parent] = useAutoAnimate<HTMLDivElement>();
  const showLabelBase =
    props.questionGroup.questions.filter(
      (question) => !isAnswerableQuestion(question) || question.type === "Document"
    ).length > 1;

  const isQuestionReadOnly = (question: HRQuestion) => {
    return match(question)
      .with({ type: "Document", complianceRejectionData: { type: "Rejected" } }, () => false)
      .with({ type: "Document", complianceRejectionData: { type: "Dismissed" } }, () => false)
      .with({ answerRejectionStatus: { status: "Rejected" } }, () => false)
      .otherwise(() => props.isReadOnly);
  };

  return (
    <Question.Group ref={parent}>
      {props.questionGroup.questions
        .filter((q) => !q.isHidden)
        .map((question, i) => (
          <OnboardingQuestion
            key={question.id.toString()}
            question={question}
            isFirst={i === 0}
            showLabel={shouldShowLabel(question, showLabelBase, props.title, props.language)}
            language={props.language}
            control={props.control}
            emailVerificationTemplateId={props.emailVerificationTemplateId}
            isReadOnly={isQuestionReadOnly(question)}
            isSignaureDisabled={props.isSignaureDisabled}
            register={props.register}
            setValue={props.setValue}
            getValues={props.getValues}
            watch={props.watch}
            onError={props.onError}
          />
        ))}
    </Question.Group>
  );
}

function shouldShowLabel(
  question: HRQuestion,
  showLabelBase: boolean,
  title: string,
  language: string
) {
  const questionText = getQuestionText(question, language);

  return showLabelBase || questionText !== title;
}

export default OnboardingQuestionGroup;
